/* eslint-disable import/default */
import chiaBls from 'chia-bls';
import chiaRpc, { type Coin, type CoinSpend } from 'chia-rpc';
import clvmLib, { Program as ProgramType } from 'clvm-lib';
/* eslint-enable import/default */
import type { SpendableCoin } from '~/composables/useGoby';
import {
  AssertCoinAnnouncement,
  CreateCoin,
  CreatePuzzleAnnouncement,
  ReserveFeeCondition,
  selectXchCoins,
  solutionForConditions,
  trim0x,
} from '~/utilities/chia';
import { DID_INNERPUZ_MOD, SINGLETON_LAUNCHER_MOD, SINGLETON_TOP_LAYER_MOD } from '~/utilities/puzzles';

/* eslint-disable import/no-named-as-default-member */
const { toHex } = chiaBls;
const { toCoinId } = chiaRpc;
const { Program } = clvmLib;

/* eslint-enable import/no-named-as-default-member */

export function createDidCoinSpends(p2Puzzle: ProgramType, inputCoinRecords: SpendableCoin[], fee = 600_000_000) {
  const { selectedCoins, changeAmount } = selectXchCoins(inputCoinRecords, fee + 1);

  const originCoinRecord = selectedCoins[0];

  const amount = 1;
  const launcherCoin: Coin = {
    parent_coin_info: `0x${originCoinRecord.coinName}`,
    amount,
    puzzle_hash: `0x${SINGLETON_LAUNCHER_MOD.hashHex()}`,
  };
  const launcherId = toCoinId(launcherCoin);

  const backupIdsHash = Program.fromBytes(Program.fromList([]).hash());
  const numOfBackupIdsNeeded = Program.fromInt(0);
  const singletonStruct = Program.cons(
    Program.fromBytes(SINGLETON_TOP_LAYER_MOD.hash()),
    Program.cons(Program.fromBytes(launcherId), Program.fromBytes(SINGLETON_LAUNCHER_MOD.hash()))
  );
  const metadata = Program.fromList([]);

  const didInner = DID_INNERPUZ_MOD.curry([p2Puzzle, backupIdsHash, numOfBackupIdsNeeded, singletonStruct, metadata]);
  const didInnerHash = didInner.hash();
  const didFullPuzzle = SINGLETON_TOP_LAYER_MOD.curry([singletonStruct, didInner]);
  const didPuzzleHash = didFullPuzzle.hash();

  const announcementMessage = Program.fromList([
    Program.fromBytes(didPuzzleHash),
    Program.fromInt(amount),
    Program.fromHex('0x80'),
  ]).hash();

  const inputCoinSolution = solutionForConditions([
    CreateCoin(SINGLETON_LAUNCHER_MOD.hash(), 1),
    CreateCoin(p2Puzzle.hash(), changeAmount),
    AssertCoinAnnouncement(launcherId, announcementMessage),
    ReserveFeeCondition(fee),
  ]);
  const inputCoinSpend: CoinSpend = {
    coin: originCoinRecord.coin,
    puzzle_reveal: p2Puzzle.serializeHex(),
    solution: inputCoinSolution.serializeHex(),
  };

  const feeCoinSpends: CoinSpend[] = selectedCoins.slice(1).map(({ coin }) => {
    const feeCoinSolution = solutionForConditions([AssertCoinAnnouncement(launcherId, announcementMessage)]);
    return {
      coin,
      puzzle_reveal: p2Puzzle.serializeHex(),
      solution: feeCoinSolution.serializeHex(),
    };
  });

  const launcherSolution = Program.fromList([
    Program.fromBytes(didPuzzleHash),
    Program.fromInt(amount),
    Program.fromHex('0x80'),
  ]);
  const launcherCoinSpend: CoinSpend = {
    coin: launcherCoin,
    puzzle_reveal: SINGLETON_LAUNCHER_MOD.serializeHex(),
    solution: launcherSolution.serializeHex(),
  };

  const eveCoin: Coin = {
    parent_coin_info: `0x${toHex(launcherId)}`,
    puzzle_hash: `0x${toHex(didPuzzleHash)}`,
    amount,
  };
  const p2Solution = solutionForConditions([CreateCoin(didInnerHash, eveCoin.amount, [p2Puzzle.hash()])]);
  const eveCoinInnerSolution = Program.fromList([Program.fromInt(1), p2Solution]);
  const eveCoinSolution = Program.fromList([
    Program.fromList([Program.fromHex(originCoinRecord.coinName), Program.fromInt(launcherCoin.amount)]),
    Program.fromInt(eveCoin.amount),
    eveCoinInnerSolution,
  ]);

  const eveCoinSpend: CoinSpend = {
    coin: eveCoin,
    puzzle_reveal: didFullPuzzle.serializeHex(),
    solution: eveCoinSolution.serializeHex(),
  };
  return [inputCoinSpend, launcherCoinSpend, eveCoinSpend, ...feeCoinSpends];
}

export function createMessageSpend(
  spendableDidCoin: SpendableCoin,
  puzzleAnnouncements: Uint8Array[]
): { didId: Uint8Array; didCoinSpend: CoinSpend; didInnerHash: Uint8Array } {
  if (!spendableDidCoin.lineageProof) {
    throw new Error('Missing lineage proof');
  }

  console.log(spendableDidCoin);
  const didPuzzle = Program.deserializeHex(spendableDidCoin.puzzle);
  const [, curriedArgs] = didPuzzle.uncurry()!;
  const didId = curriedArgs[0].rest.first.toBytes();
  const didInnerPuzzle = curriedArgs[1];
  const [, innerCurriedArgs] = didInnerPuzzle.uncurry()!;
  const p2Puzzle = innerCurriedArgs[0];

  const didCoin: Coin = spendableDidCoin.coin;
  const conditions = [
    CreateCoin(didInnerPuzzle.hash(), didCoin.amount, [p2Puzzle.hash()]),
    ...puzzleAnnouncements.map((message) => CreatePuzzleAnnouncement(message)),
  ];
  const p2Solution = solutionForConditions(conditions);
  const innerSolution = Program.fromList([Program.fromInt(1), p2Solution]);
  const fullSolution = Program.fromList([
    Program.fromList([
      Program.fromHex(trim0x(spendableDidCoin.lineageProof.parentName!)),
      Program.fromHex(trim0x(spendableDidCoin.lineageProof.innerPuzzleHash!)),
      Program.fromInt(spendableDidCoin.lineageProof.amount!),
    ]),
    Program.fromInt(didCoin.amount),
    innerSolution,
  ]);
  return {
    didId,
    didInnerHash: didInnerPuzzle.hash(),
    didCoinSpend: {
      coin: didCoin,
      puzzle_reveal: `0x${spendableDidCoin.puzzle}`,
      solution: fullSolution.serializeHex(),
    },
  };
}
